import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { tabelaPrecoService, SalvarTabelaPrecoDto } from "services/tabelaPrecoService";

// Query keys
export const tabelaPrecoKeys = {
  all: ['tabelaPreco'] as const,
  list: (termo?: string) => [...tabelaPrecoKeys.all, 'list', { termo }] as const,
  detail: (id: string) => [...tabelaPrecoKeys.all, 'detail', id] as const,
};

// Query para obter todas as tabelas de preço
export const useTabelasPreco = (termo?: string) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: tabelaPrecoKeys.list(termo),
    queryFn: () => tabelaPrecoService.listar(termo),
    retry: getQueryRetryHandler({
      queryKey: tabelaPrecoKeys.list(termo),
      defaultMessage: "Você não possui permissão para consultar tabelas de preço."
    }),
  });
};

// Query para obter uma tabela de preço específica
export const useTabelaPreco = (id: string, enabled: boolean = true) => {
  const { getQueryRetryHandler } = usePermissionModal();

  return useQuery({
    queryKey: tabelaPrecoKeys.detail(id),
    queryFn: () => tabelaPrecoService.obter(id),
    enabled: !!id && enabled,
    retry: getQueryRetryHandler({
      queryKey: tabelaPrecoKeys.detail(id),
      defaultMessage: "Você não possui permissão para consultar esta tabela de preço."
    }),
  });
};

// Mutation para criar tabela de preço
export const useCriarTabelaPreco = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (data: SalvarTabelaPrecoDto) => tabelaPrecoService.criar(data),
    onSuccess: () => {
      // Invalidar todas as queries de tabelas de preço
      queryClient.invalidateQueries({ queryKey: tabelaPrecoKeys.all });
    },
    onError: getMutationErrorHandler("Você não possui permissão para criar tabelas de preço."),
  });
};

// Mutation para alterar tabela de preço
export const useAlterarTabelaPreco = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SalvarTabelaPrecoDto }) =>
      tabelaPrecoService.alterar(id, data),
    onSuccess: (_, { id }) => {
      // Invalidar queries específicas
      queryClient.invalidateQueries({ queryKey: tabelaPrecoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tabelaPrecoKeys.all });
    },
    onError: getMutationErrorHandler("Você não possui permissão para alterar tabelas de preço."),
  });
};

// Mutation para remover tabela de preço
export const useRemoverTabelaPreco = () => {
  const queryClient = useQueryClient();
  const { getMutationErrorHandler } = usePermissionModal();

  return useMutation({
    mutationFn: (id: string) => tabelaPrecoService.remover(id),
    onSuccess: (_, id) => {
      // Invalidar queries
      queryClient.invalidateQueries({ queryKey: tabelaPrecoKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: tabelaPrecoKeys.all });
    },
    onError: getMutationErrorHandler("Você não possui permissão para remover tabelas de preço."),
  });
};
